'use client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ShieldCheck } from 'lucide-react'
import { Sparkline } from './sparkline'

export interface PillarItem {
  code: string              // 'PF1'..'PF5'
  letter: string            // 'P', 'H', 'I', 'S', 'H'
  label: string             // 'Pressure', etc.
  mastery: number           // 0..100
  spark: number[]           // recent history
  color: string             // pillar hex
}

export interface PillarMasteryStripProps {
  pillars: PillarItem[]
}

export function PillarMasteryStrip({ pillars }: PillarMasteryStripProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" /> P.H.I.S.H. Pillar mastery
        </CardTitle>
        <span className="text-xs text-muted-foreground">7-day trend</span>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {pillars.map((p) => {
            const trend = p.spark[p.spark.length - 1] - p.spark[0]
            const trendStr = trend > 0 ? `+${trend}` : `${trend}`
            const trendColor = trend > 0 ? 'text-emerald-500 dark:text-emerald-400' : trend < 0 ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground'
            return (
              <div key={p.code} className="relative overflow-hidden rounded-xl border border-border bg-muted/40 p-3">
                <div className="flex items-start justify-between">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-white text-lg font-bold shadow"
                    style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}aa)` }}
                  >
                    {p.letter}
                  </div>
                  <Badge variant="outline" className="text-[10px]">{p.code}</Badge>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground truncate">{p.label}</div>
                <div className="mt-0.5 flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold tabular-nums text-foreground">{p.mastery}</span>
                  <span className="text-[10px] text-muted-foreground">/ 100</span>
                  <span className={`text-[10px] font-semibold tabular-nums ml-auto ${trendColor}`}>{trendStr}</span>
                </div>
                <div className="mt-1 -mx-1">
                  <Sparkline data={p.spark} color={p.color} height={30} />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
